import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query-v5';
import api from '../api';
import { QueryKey } from '../enums';
import { useAgentApi } from '../hooks/use-agent-api';

export type ExternalTokenRequestDto = {
  orgId: string;
};

export type ExternalTokenResponseDto = {
  access_token?: string;
  token_type?: string;
};

export interface AgentDocumentDto {
  doc_id: string;
  doc_type: string | null;
  display_name: string | null;
  document_hash: string;
  duplicate_of: string | null;
  state: string;
  filename: string;
  user: string | null;
  spend_category: string | null;
  matching_document: number | null;
  upload_date: string | null;
  vendor: string | null;
  subsidiary: string | null;
  start_date: string | null;
  end_date: string | null;
  anomaly: boolean | null;
}

export type AgentDocumentsResponseDto = AgentDocumentDto[];

const CACHE_TIME = 5 * 60 * 1000;

export const useExternalTokenAuth = (
  organizationId: string,
  aiPort?: string,
  options?: UseMutationOptions<
    ExternalTokenResponseDto,
    Error,
    ExternalTokenRequestDto
  >
) => {
  const agentApi = useAgentApi(aiPort);

  return useMutation({
    ...options,
    mutationFn: async (data: ExternalTokenRequestDto) => {
      const franzToken =
        await api.token.tokenControllerGetFranzOboToken(organizationId);

      const response = await agentApi.instance.get<ExternalTokenResponseDto>(
        '/auth/external-token',
        {
          params: {
            org_id: data.orgId,
          },
          headers: {
            Authorization: `Bearer ${franzToken}`,
          },
          withCredentials: true,
        }
      );

      return response.data;
    },
  });
};

export const useAgentDocumentsList = (
  externalToken?: string,
  aiPort?: string,
  options?: Partial<UseQueryOptions<AgentDocumentsResponseDto>>
) => {
  const agentApi = useAgentApi(aiPort);

  return useQuery({
    queryKey: [
      QueryKey.Resource.franzAI,
      QueryKey.Operation.List,
      externalToken,
      aiPort, // Include aiPort in queryKey to ensure cache separation
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () => {
      const params = externalToken ? { token: externalToken } : undefined;

      const response = await agentApi.instance.get<AgentDocumentsResponseDto>(
        '/documents',
        {
          params,
          withCredentials: true,
        }
      );

      return response.data;
    },
    retry: 2,
    ...options,
  });
};
